import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import {
  FRAME_INTERVAL_MS,
  calibrateBackgroundCategory,
  initialFrameBudget,
  observeFrameTime,
} from "./backgroundBlurPolicy.js";
import type { FrameBudget } from "./backgroundBlurPolicy.js";
import type { MaskFrame, SelfieSegmenter } from "./backgroundSegmenter.js";

/**
 * Candidate · REAL background blur for the interview self-view.
 *
 * WHY THIS EXISTS: the previous implementation put `filter: blur(14px)` on the whole <video>,
 * which blurred the CANDIDATE'S FACE. In a proctored, recorded interview that is actively
 * harmful — the recruiter can't see who they're talking to, and any camera-side presence check
 * is blinded. Background blur has to mean what it says: segment the person, blur only what is
 * behind them.
 *
 * THREE INVARIANTS, in priority order:
 *
 *  1. NEVER blur the person. If we cannot separate them from the background we turn the feature
 *     OFF and say why. There is no fall back to whole-frame blur — that IS the bug.
 *  2. NEVER touch the recording. useRecorder records `cam.stream`'s original video track. This
 *     hook only ever paints an extra layer on top of the preview, or (native path) hands the
 *     preview a CLONE. The recruiter always reviews real, unblurred frames; blur is a comfort
 *     feature, not a redaction. useProctoring's face sampler likewise keeps reading the raw
 *     <video>, so it is never blinded either.
 *  3. NEVER stutter the interview. Segmentation shares a laptop with MediaRecorder and
 *     SpeechRecognition, so it is capped at 15 fps and watched: if frames cost more than the
 *     budget for a sustained stretch, we disable rather than degrade the whole session.
 *
 * TWO ROUTES, cheapest first:
 *
 *  - NATIVE: `applyConstraints({ backgroundBlur: true })` (Media Capture camera effects — today
 *    Windows Studio Effects and ChromeOS). Zero download, zero CPU, done by the OS. Rare, so we
 *    check for it but never depend on it.
 *  - SEGMENTATION: MediaPipe selfie segmentation composited on a canvas — blurred frame first,
 *    then the person's masked pixels on top. Lazily downloaded only when the toggle is used.
 *
 * (`MediaStreamTrackProcessor` is deliberately not used: it is a frame TRANSPORT, not an effect,
 * so it would buy us nothing over `drawImage(video)` while cutting out Safari and Firefox.)
 */

export type BlurStatus =
  | "off" // toggle off — the raw self-view is what's on screen
  | "starting" // negotiating / downloading the model / calibrating
  | "native" // the camera itself is doing it, on a cloned track
  | "canvas" // our segmentation composite is the visible preview
  | "unavailable"; // can't do it honestly on this device — toggle disabled, `reason` says why

// --- tuning ------------------------------------------------------------------------------
// The frame rate cap, the frame budget and the mask-polarity rules live in backgroundBlurPolicy
// (pure + unit-tested — they're the two things that must never be wrong). What's left here is
// purely about pixels.
const CALIBRATION_FRAMES = 45; // ~3s to find a frame we can read mask polarity from
const MAX_WIDTH = 640; // cap the composite; a self-view thumbnail never needs more
const BG_BLUR_PX = 12;
const EDGE_FEATHER_PX = 3; // softens the hard category edge so the cut-out doesn't look pasted
const OVERSCAN = 0.06; // draw the background slightly oversized so blur doesn't sample past the
// frame edge and leave a dark vignette

// Honest, candidate-facing reasons. Never blame the candidate, always say what we did instead.
const REASON = {
  browser: "Background blur isn't supported by this browser. Your camera keeps working normally.",
  model: "We couldn't load background blur just now, so it stays off. Everything else is unaffected.",
  slow: "Background blur was using too much of your device, so we switched it off to keep your interview smooth.",
  separate:
    "We couldn't tell you apart from your background, so blur stays off — we won't blur your face. Make sure you're in frame and try again.",
} as const;

/** Canvas 2D `filter` — the whole composite depends on it. Safari only shipped it in 16.4. */
function canvasFilterSupported(): boolean {
  try {
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) return false;
    ctx.filter = "blur(2px)";
    return ctx.filter === "blur(2px)";
  } catch {
    return false;
  }
}

/** The smallest valid SIMD module — we ship only the SIMD wasm, so probe before downloading it. */
const SIMD_PROBE = new Uint8Array([
  0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
]);
function simdSupported(): boolean {
  try {
    return typeof WebAssembly !== "undefined" && WebAssembly.validate(SIMD_PROBE);
  } catch {
    return false;
  }
}

/**
 * The platform's own background blur, applied to a CLONE of the camera track.
 *
 * RECORDING SAFETY: a clone shares one underlying camera source, and a platform is free to apply
 * an effect source-wide rather than per-track — which would silently blur the recruiter's
 * recording, i.e. redact evidence behind a comfort toggle. So we only accept this route when the
 * ORIGINAL track still explicitly reports `backgroundBlur: false` afterwards. Anything else
 * (true, or the key missing, so we can't prove it) is treated as unsafe and we fall through to
 * canvas segmentation, which cannot touch the source at all.
 */
async function tryNativeBlur(source: MediaStream | null): Promise<{ stream: MediaStream; dispose: () => void } | null> {
  try {
    const track = source?.getVideoTracks()[0];
    if (!track) return null;
    const supported = navigator.mediaDevices?.getSupportedConstraints?.() as Record<string, unknown> | undefined;
    if (supported?.["backgroundBlur"] !== true) return null;
    const caps = (track.getCapabilities?.() ?? {}) as { backgroundBlur?: boolean[] };
    if (!Array.isArray(caps.backgroundBlur) || !caps.backgroundBlur.includes(true)) return null;

    const clone = track.clone();
    try {
      await clone.applyConstraints({ backgroundBlur: true } as unknown as MediaTrackConstraints);
    } catch {
      clone.stop();
      return null;
    }
    const cloneBlur = (clone.getSettings() as { backgroundBlur?: boolean }).backgroundBlur;
    const originalBlur = (track.getSettings() as { backgroundBlur?: boolean }).backgroundBlur;
    if (cloneBlur !== true || originalBlur !== false) {
      clone.stop();
      return null;
    }
    return { stream: new MediaStream([clone]), dispose: () => clone.stop() };
  } catch {
    return null;
  }
}

/** Lazily-created 2D scratch surfaces, kept for the life of the hook. */
interface Scratch {
  mask: HTMLCanvasElement;
  maskCtx: CanvasRenderingContext2D;
  person: HTMLCanvasElement;
  personCtx: CanvasRenderingContext2D;
  maskImage: ImageData | null;
}

function makeScratch(): Scratch | null {
  const mask = document.createElement("canvas");
  const person = document.createElement("canvas");
  const maskCtx = mask.getContext("2d", { willReadFrequently: true });
  const personCtx = person.getContext("2d");
  if (!maskCtx || !personCtx) return null;
  return { mask, maskCtx, person, personCtx, maskImage: null };
}

/** Blurred background + the person's real pixels on top. Nothing here touches the camera track. */
function composite(
  video: HTMLVideoElement,
  frame: MaskFrame,
  backgroundCategory: number,
  out: HTMLCanvasElement,
  s: Scratch,
): void {
  const scale = Math.min(1, MAX_WIDTH / video.videoWidth);
  const w = Math.max(1, Math.round(video.videoWidth * scale));
  const h = Math.max(1, Math.round(video.videoHeight * scale));

  // 1) the mask, as an alpha stencil at the model's own resolution.
  const { data, width: mw, height: mh } = frame;
  if (s.mask.width !== mw || s.mask.height !== mh) {
    s.mask.width = mw;
    s.mask.height = mh;
    s.maskImage = null;
  }
  let img = s.maskImage;
  if (!img) {
    img = s.maskCtx.createImageData(mw, mh);
    // RGB is constant white; only alpha changes per frame, so fill it once.
    img.data.fill(255);
    s.maskImage = img;
  }
  const px = img.data;
  for (let i = 0, p = 3; i < mw * mh; i++, p += 4) px[p] = data[i] === backgroundCategory ? 0 : 255;
  s.maskCtx.putImageData(img, 0, 0);

  // 2) the person, cut out of the real frame by that stencil (feathered so the edge isn't a
  //    scissor line).
  if (s.person.width !== w || s.person.height !== h) {
    s.person.width = w;
    s.person.height = h;
  }
  const pctx = s.personCtx;
  pctx.globalCompositeOperation = "source-over";
  pctx.filter = "none";
  pctx.clearRect(0, 0, w, h);
  pctx.drawImage(video, 0, 0, w, h);
  pctx.globalCompositeOperation = "destination-in";
  pctx.filter = `blur(${EDGE_FEATHER_PX}px)`;
  pctx.drawImage(s.mask, 0, 0, w, h);
  pctx.filter = "none";
  pctx.globalCompositeOperation = "source-over";

  // 3) blurred background, then the person over it.
  if (out.width !== w || out.height !== h) {
    out.width = w;
    out.height = h;
  }
  const octx = out.getContext("2d");
  if (!octx) return;
  octx.globalCompositeOperation = "source-over";
  octx.filter = `blur(${BG_BLUR_PX}px)`;
  const ox = w * OVERSCAN;
  const oy = h * OVERSCAN;
  octx.drawImage(video, -ox, -oy, w + ox * 2, h + oy * 2);
  octx.filter = "none";
  octx.drawImage(s.person, 0, 0);
}

export interface BackgroundBlur {
  /** What the candidate asked for. */
  on: boolean;
  status: BlurStatus;
  /** Plain-English explanation, set only when `status === "unavailable"`. */
  reason: string | null;
  /** True once blur is known to be impossible on this device — render the toggle disabled. A
   *  failure the candidate can actually do something about (framing) leaves this false so the
   *  "try again" in `reason` is honest. */
  disabled: boolean;
  /** The composited preview. Rendered on top of the raw self-view while `status === "canvas"`. */
  canvasRef: RefObject<HTMLCanvasElement | null>;
  /** The platform-blurred clone. Rendered on top while `status === "native"`. */
  nativeVideoRef: RefObject<HTMLVideoElement | null>;
  toggle: () => void;
}

export function useBackgroundBlur(
  videoRef: RefObject<HTMLVideoElement | null>,
  stream: MediaStream | null,
): BackgroundBlur {
  const [on, setOn] = useState(false); // OFF by default — this is opt-in comfort, not policy
  const [status, setStatus] = useState<BlurStatus>("off");
  const [reason, setReason] = useState<string | null>(null);
  // Some failures are facts about the device (no SIMD, too slow) and are final; "we couldn't
  // tell you apart from your background" is about how the candidate happened to be sitting, so
  // that one leaves the toggle live — the copy tells them to try again and it has to be true.
  const [retryable, setRetryable] = useState(false);
  const [nativeStream, setNativeStream] = useState<MediaStream | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const nativeVideoRef = useRef<HTMLVideoElement | null>(null);
  const segmenterRef = useRef<SelfieSegmenter | null>(null);
  const nativeRef = useRef<{ stream: MediaStream; dispose: () => void } | null>(null);
  const scratchRef = useRef<Scratch | null>(null);
  const rafRef = useRef<number | null>(null);

  // Per-run counters (reset whenever the loop starts).
  const bgCategoryRef = useRef<number | null>(null);
  const calibrationTriesRef = useRef(0);
  const budgetRef = useRef<FrameBudget>(initialFrameBudget());
  const errorsRef = useRef(0);
  const lastRunRef = useRef(0);
  const lastStampRef = useRef(0);

  // Latest-value refs so the rAF loop never needs to be torn down and rebuilt on a state change.
  const videoRefLatest = useRef(videoRef);
  videoRefLatest.current = videoRef;

  const fail = useCallback((why: string, canRetry = false): void => {
    setReason(why);
    setRetryable(canRetry);
    setStatus("unavailable");
    setOn(false); // flipping this off tears the loop down through the effect below
  }, []);

  const toggle = useCallback((): void => {
    setOn((v) => !v);
  }, []);

  // Keep the native <video> fed. It stays mounted (hidden) so the element exists before we have
  // a stream for it; autoplay on a display:none element is unreliable, hence the explicit play().
  useEffect(() => {
    const el = nativeVideoRef.current;
    if (!el) return;
    el.srcObject = nativeStream;
    if (nativeStream) void el.play().catch(() => {});
  }, [nativeStream, status]);

  useEffect(() => {
    if (!on) {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      nativeRef.current?.dispose();
      nativeRef.current = null;
      setNativeStream(null);
      // Only "unavailable" survives being switched off — it's a standing fact about this device.
      setStatus((s) => (s === "unavailable" ? s : "off"));
      return;
    }

    let cancelled = false;
    setStatus("starting");
    setReason(null);
    setRetryable(false);

    const loop = (): void => {
      rafRef.current = requestAnimationFrame(loop);
      const now = performance.now();
      if (now - lastRunRef.current < FRAME_INTERVAL_MS) return;
      lastRunRef.current = now;

      const video = videoRefLatest.current.current;
      const segmenter = segmenterRef.current;
      const out = canvasRef.current;
      const scratch = scratchRef.current;
      if (!video || !segmenter || !out || !scratch) return;
      if (video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) return;

      const started = performance.now();
      let frame: MaskFrame | null;
      try {
        // segmentForVideo demands strictly increasing timestamps.
        const stamp = Math.max(lastStampRef.current + 1, Math.round(now));
        lastStampRef.current = stamp;
        frame = segmenter.segment(video, stamp);
      } catch {
        errorsRef.current += 1;
        if (errorsRef.current > 5) fail(REASON.model);
        return;
      }
      errorsRef.current = 0; // consecutive failures are the signal, not a lifetime tally
      if (!frame) return;

      if (bgCategoryRef.current === null) {
        const category = calibrateBackgroundCategory(frame);
        if (category === null) {
          calibrationTriesRef.current += 1;
          // Recoverable: this is about how they're framed right now, not about the device.
          if (calibrationTriesRef.current > CALIBRATION_FRAMES) fail(REASON.separate, true);
          return; // still showing the raw, unblurred self-view — never a half-guessed composite
        }
        bgCategoryRef.current = category;
        setStatus("canvas"); // only now does the composite become the visible preview
      }

      composite(video, frame, bgCategoryRef.current, out, scratch);

      // Frame-time watchdog: if this device can't hold the rate, switch blur off rather than
      // stutter the interview it's sharing a CPU with.
      const { next, tooSlow } = observeFrameTime(budgetRef.current, performance.now() - started);
      budgetRef.current = next;
      if (tooSlow) fail(REASON.slow);
    };

    void (async () => {
      // 1) Free route first: let the camera do it, if the platform can prove it left the
      //    recorded track alone.
      const native = await tryNativeBlur(stream);
      if (cancelled) {
        native?.dispose();
        return;
      }
      if (native) {
        nativeRef.current = native;
        setNativeStream(native.stream);
        setStatus("native");
        return;
      }

      // 2) Segmentation. Probe the cheap prerequisites before pulling down ~11 MB of wasm.
      if (!canvasFilterSupported() || !simdSupported()) {
        fail(REASON.browser);
        return;
      }
      if (!segmenterRef.current) {
        try {
          const mod = await import("./backgroundSegmenter.js");
          const segmenter = await mod.createSelfieSegmenter();
          if (cancelled) {
            segmenter.close();
            return;
          }
          segmenterRef.current = segmenter;
        } catch {
          fail(REASON.model);
          return;
        }
      }
      if (cancelled) return;
      if (!scratchRef.current) scratchRef.current = makeScratch();
      if (!scratchRef.current) {
        fail(REASON.browser);
        return;
      }

      bgCategoryRef.current = null;
      calibrationTriesRef.current = 0;
      budgetRef.current = initialFrameBudget();
      errorsRef.current = 0;
      lastRunRef.current = 0;
      rafRef.current = requestAnimationFrame(loop);
    })();

    return () => {
      cancelled = true;
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      // Drop the cloned camera track here too, not only on the off-branch: if this effect
      // re-runs while still enabled (a new camera stream), the previous clone would otherwise
      // be orphaned and hold the camera open behind the candidate's back.
      nativeRef.current?.dispose();
      nativeRef.current = null;
    };
  }, [on, stream, fail]);

  // The segmenter is kept alive across toggles (re-enabling is then instant and re-downloads
  // nothing) but must not outlive the interview screen — it holds a GPU context.
  useEffect(
    () => () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      segmenterRef.current?.close();
      segmenterRef.current = null;
      nativeRef.current?.dispose();
      nativeRef.current = null;
    },
    [],
  );

  return useMemo(
    () => ({
      on,
      status,
      reason,
      disabled: status === "unavailable" && !retryable,
      canvasRef,
      nativeVideoRef,
      toggle,
    }),
    [on, status, reason, retryable, toggle],
  );
}
